import React, {useState, useEffect, Suspense, lazy} from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import Markup from './markup/markup';
import LogoSchema from "./markup/elements/logo-schema";
import BookingDecisionGate from "./markup/elements/BookingDecisionGate";
import RouterDirect from "./Router";
import './App.scss';
import './vendor/font-awesome/css/font-awesome.min.css';
import { getLocalItem, getSessionItem, purgeExpiredLocal, purgeExpiredSession, setSessionItem } from './utils/storage';
import { initMetaPixel } from './utils/metaPixel';
import { pageView as analyticsPageView, identify as analyticsIdentify } from './utils/analytics';

const ATTR_STORAGE_KEY =
  process.env.REACT_APP_ATTR_STORAGE_KEY;
const SESSION_ID_KEY = process.env.REACT_APP_SESSION_ID_KEY;

const isSSR = typeof window === 'undefined';

const getOrCreateSessionId = () => {
  if (isSSR) return '';
  try {
    const existing = getSessionItem(SESSION_ID_KEY);
    if (existing) return existing;
    const newId =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setSessionItem(SESSION_ID_KEY, newId);
    return newId;
  } catch (e) {
    return '';
  }
};

// Lazy load non-critical CSS for better performance
const loadNonCriticalCSS = () => {
  // Load Slick Carousel CSS only when needed
  import("slick-carousel/slick/slick.css");
  import("slick-carousel/slick/slick-theme.css");
  // Load Modal Video CSS only when needed
  import('react-modal-video/css/modal-video.min.css');
};

// Lazy load Router for better initial load (direct import for SSR — React 17 doesn't support Suspense in renderToString)
const Router = isSSR ? RouterDirect : lazy(() => import("./Router"));

// Lazy load external script libraries
const TagManager = lazy(() => import('react-gtm-module'));

// Lazy load the chatbot component for better performance
const Chatbot = isSSR ? () => null : lazy(() => import("./markup/elements/chatbot"));

// Component to conditionally render chatbot based on route
function ChatbotWrapper({ showChatBot, isMobileApp, user, setUser }) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Hide chatbot on mechanic pages + the focused QR verify / app-download microsite pages
  const hideChatbotPaths = ['/mechanic-login', '/mechanic-kyc', '/mechanic-dashboard', '/u/', '/app'];
  const shouldHideChatbot = hideChatbotPaths.some(path => currentPath.startsWith(path));

  if (!showChatBot || shouldHideChatbot) {
    return null;
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Chatbot isMobileApp={isMobileApp} user={user} setUser={setUser} />
    </Suspense>
  );
}

// Booking Decision Gate: blocking YES/NO modal for an open booking offer.
// App-level so it covers every mechanic page (dashboard + booking details).
function BookingDecisionGateWrapper() {
  const location = useLocation();
  if (!location.pathname.startsWith('/mechanic-dashboard')) return null;
  return <BookingDecisionGate />;
}

// Tracks every React Router transition and fires a `view` analytics event.
// Mounted inside <Router> so `useLocation()` is available. Skips the first
// render on the initial path (that one is fired by App's mount-effect to
// avoid double-counting).
function RouteChangeTracker() {
  const location = useLocation();
  const lastPathRef = React.useRef(null);
  React.useEffect(() => {
    if (lastPathRef.current === location.pathname) return;
    if (lastPathRef.current !== null) {
      try {
        analyticsPageView(location.pathname);
      } catch (_err) {
        /* swallow */
      }
    }
    lastPathRef.current = location.pathname;
  }, [location.pathname]);
  return null;
}

function App(props) {

	const { ssrLocation, isMobileBrowser: initialIsMobileBrowser } = props;
	
	const [user, setUser] = useState({ is_logged: false, email: '', fn: '', ln: '', ph: '' });
	const [userVehicles, setUserVehicles] = useState([]);
	const [openModal, setOpenModal] = useState(() => {
		// Respect previous dismissal: don't re-show modal if user already
		// provided phone (localStorage, 24h) or dismissed it (sessionStorage).
		if (typeof window !== 'undefined') {
			if (getLocalItem('phone_provided') === 'yes') return false;
			if (getSessionItem('phone_ignored') === 'yes') return false;
		}
		return true;
	});
	const [citiesModal, setCitiesModal] = useState(false);
	const [isMobileApp, setIsMobileApp] = useState(false);
	const [isMobileBrowser, setIsMobileBrowser] = useState(initialIsMobileBrowser);
  const [showChatBot, setShowChatBot] = useState(false); // Start with false for better performance
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);

	useEffect(() => {
    // Function to handle window resize with debouncing
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const isMobile = window.innerWidth <= 1024;
        setIsMobileBrowser(isMobile);
      }, 100);
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Call handler once to set the initial state based on current window size
    handleResize();

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Boot-hydration keystone: restore the logged-in customer from storage on every
  // load (client-only, zero network, SSR-safe). Mirrors the exact fallback read
  // already used in booking-history.jsx so identity is consistent site-wide.
  // Purely additive — touches no booking/write path.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      let userPhone = getLocalItem('userPhone');
      let details = null;
      const rawDetails = getLocalItem('userDetails');
      if (rawDetails) {
        try { details = JSON.parse(rawDetails); } catch (e) { details = null; }
      }
      if (!userPhone && details) userPhone = details.phone;
      if (userPhone) {
        setUser({
          is_logged: true,
          ph: userPhone,
          email: (details && details.email) || getLocalItem('userEmail') || '',
          fn: (details && details.first_name) || '',
          ln: (details && details.last_name) || '',
        });
      }
    } catch (e) {
      // ignore — never block boot on hydration
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Enforce browser TTL.
      purgeExpiredLocal();
      purgeExpiredSession();

      // Capture attribution once per session as early as possible (independent of modal opening).
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const gclid = urlParams.get('gclid');
        const fbclid = urlParams.get('fbclid');
        const utm_source = urlParams.get('utm_source');
        const utm_medium = urlParams.get('utm_medium');
        const utm_campaign = urlParams.get('utm_campaign');
        const session_id = getOrCreateSessionId();
        const existingRaw = getSessionItem(ATTR_STORAGE_KEY);
        const existing = existingRaw ? JSON.parse(existingRaw) : null;
        const capturedAt = new Date().toLocaleString('sv-SE', {
          timeZone: 'Asia/Kolkata',
          hour12: false,
        });

        const baseAttribution = {
          session_id: session_id || null,
          captured_at: capturedAt,
          landing_url: window.location.href,
          referrer: document.referrer || '',
        };

        const incoming = {
          gclid: gclid || null,
          fbclid: fbclid || null,
          utm_source: utm_source || null,
          utm_medium: utm_medium || null,
          utm_campaign: utm_campaign || null,
        };

        const hasIncoming = Boolean(gclid || fbclid || utm_source || utm_medium || utm_campaign);
        if (!existing) {
          setSessionItem(
            ATTR_STORAGE_KEY,
            JSON.stringify({
              ...baseAttribution,
              ...incoming,
            })
          );
        } else {
          const merged = { ...baseAttribution, ...existing };
          if (hasIncoming) {
            Object.entries(incoming).forEach(([key, v]) => {
              if (v && !merged[key]) {
                merged[key] = v;
              }
            });
          }
          setSessionItem(ATTR_STORAGE_KEY, JSON.stringify(merged));
        }
        if (gclid) {
          setSessionItem(process.env.REACT_APP_GCLID_SESSION_KEY, gclid);
        }
      } catch (e) {
        // ignore
      }

      // Initialize dataLayer only once
      if (!window.dataLayer) {
        window.dataLayer = [];
      }

      window.dataLayer.push({
        event: 'PageView'
      });

      // Capture-everything analytics (per CEO 2026-05-25): additive `view`
      // event for the initial page. Per-route changes are picked up by the
      // RouteChangeTracker mounted inside <Router>.
      try {
        analyticsPageView(window.location.pathname);
        // If we already have a logged-in customer (from the boot-hydrate effect),
        // stamp them onto the analytics session so the very first events carry
        // a customer_phone partition.
        const existingPhone = getLocalItem('userPhone');
        if (existingPhone) analyticsIdentify(existingPhone);
      } catch (_err) {
        // never block boot
      }
      
      const isMobileApp = typeof window !== 'undefined' && window.ReactNativeWebView !== undefined;
      setIsMobileApp(isMobileApp);
      
      // Load non-critical CSS after initial render
      setTimeout(() => {
        loadNonCriticalCSS();
      }, 1000);
      
      // Show chatbot after a delay for better performance
      setTimeout(() => {
        setShowChatBot(true);
      }, 2000);
    }

  }, []);

  // Initialize Google Tag Manager and Facebook Pixel with error handling and delay
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        // Use requestIdleCallback for analytics loading
        const loadAnalyticsWhenIdle = () => {
          import('react-gtm-module').then(({ default: TagManager }) => {
            // Initialize Google Tag Manager
            const tagManagerArgs = {
              gtmId: process.env.REACT_APP_GTM_ID
            };
            TagManager.initialize(tagManagerArgs);

            // Initialize Meta Pixel (non-blocking, singleton)
            initMetaPixel();

            setAnalyticsLoaded(true);
          });
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(loadAnalyticsWhenIdle, { timeout: 3000 });
        } else {
          setTimeout(loadAnalyticsWhenIdle, 3000);
        }
      } catch (error) {
        console.error('Error initializing analytics:', error);
      }
    };
    
    loadAnalytics();
  }, []);
	
	return (
    <>
      {!isSSR && (
        <Helmet>
          {/* Defer non-critical scripts */}
          {analyticsLoaded && (
            <script
              defer
              src={`https://www.googletagmanager.com/gtm.js?id=${process.env.REACT_APP_GTM_ID}`}
            ></script>
          )}
          {analyticsLoaded && (
            <script
              defer
              src="https://connect.facebook.net/en_US/fbevents.js"
            ></script>
          )}
        </Helmet>
      )}
      
      {isSSR ? (
        <Router ssrLocation={ssrLocation}>
          <div className="page-wraper">
            <LogoSchema />
            <Markup setShowChatBot={setShowChatBot} isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} citiesModal={citiesModal} setCitiesModal={setCitiesModal} openModal={openModal} setOpenModal={setOpenModal} user={user} setUser={setUser} userVehicles={userVehicles} setUserVehicles={setUserVehicles} />
          </div>
        </Router>
      ) : (
        <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
          <Router ssrLocation={ssrLocation}>
            <RouteChangeTracker />
            <div className="page-wraper">
              <LogoSchema />
              <Markup setShowChatBot={setShowChatBot} isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} citiesModal={citiesModal} setCitiesModal={setCitiesModal} openModal={openModal} setOpenModal={setOpenModal} user={user} setUser={setUser} userVehicles={userVehicles} setUserVehicles={setUserVehicles} />
            </div>
            <ChatbotWrapper showChatBot={showChatBot} isMobileApp={isMobileApp} user={user} setUser={setUser} />
            <BookingDecisionGateWrapper />
          </Router>
        </Suspense>
      )}
    </>
	);
}

export default App;